"use client"

import type { EventSubscriptionStatus } from "@/modules/events/client/types"
import type { TrackEventProperties } from "@/modules/tracking/types"
import type { FunctionComponent, PropsWithChildren } from "react"

import { isBefore } from "date-fns"

import { Button as SparkButton } from "@jobteaser/spark/components/Button"
import { ArrowRight } from "@jobteaser/spark/components/Icons/ArrowRight"
import { CalendarPlus } from "@jobteaser/spark/components/Icons/CalendarPlus"

import { Link } from "@/modules/routing/components/Link"
import { TrackButton } from "@/modules/tracking/components/TrackButton"

import styles from "./Button.module.css"

type ButtonProps = PropsWithChildren<{
  buttonLabel: string
  dataTestid: string
  registrationDeadline: string | null
  subscriptionStatus?: EventSubscriptionStatus
  trackEventClick?: TrackEventProperties
  url: string
}>

export const Button: FunctionComponent<ButtonProps> = ({
  buttonLabel,
  dataTestid,
  registrationDeadline,
  subscriptionStatus,
  trackEventClick,
  url,
}) => {
  const isRegistrationOpen = registrationDeadline && isBefore(new Date(), new Date(registrationDeadline))

  if (!isRegistrationOpen) {
    return null
  }

  const icon =
    isRegistrationOpen && subscriptionStatus !== "confirmed" ? (
      <CalendarPlus className={styles.icon} aria-hidden size={16} />
    ) : (
      <ArrowRight className={styles.icon} aria-hidden size={16} />
    )

  if (!trackEventClick) {
    return (
      <SparkButton
        aria-label={buttonLabel}
        className={styles.button}
        data-testid={`${dataTestid}-reco-jobs-see-more`}
        href={url}
        renderComponent={Link}
      >
        {icon}
        <span className={styles.buttonLarge}>{buttonLabel}</span>
      </SparkButton>
    )
  }

  return (
    <TrackButton
      renderComponent={Link}
      href={url}
      data-testid={`${dataTestid}-reco-jobs-see-more`}
      className={styles.button}
      aria-label={buttonLabel}
      trackingProps={trackEventClick}
    >
      {icon}
      <span className={styles.buttonLarge}>{buttonLabel}</span>
    </TrackButton>
  )
}
